// src/pages/ESimPackagesPage.js
//
// Ülke paketleri: standart ailenin tek ülkelik (scope=country) bundle'ları.
//
// Veri /v2/admin/bundles ucundan gelir. Eski /admin/countries-data bundle id
// taşımadığı için bu sayfadan paket ne yayına alınabiliyor ne Play'e
// basılabiliyordu. İki uç aynı paket kümesini döndürür; tek fark, birden çok
// ülkeyi kapsayan paketlerin (… Carib) eskiden her ülkenin altında ayrı kart
// olarak tekrar etmesiydi — burada her paket tek kart.
import React, { useState, useEffect, useCallback, useMemo, useDeferredValue } from 'react';
import styles from './ESimPackagesPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import BundleControls from '../components/catalog/BundleControls';
import { toast } from 'react-toastify';
import {
    fetchAdminBundles,
    updateBundlePlan,
    buildPlanPatchFromEdit,
    readApiError,
    formatDataAmount,
    formatMoney,
    sortPlans,
} from '../services/catalogApi';

// Katalogun tamamı tek sayfada çiziliyordu: 170 ürün kartı = 680 tablo satırı,
// ~17.600 DOM düğümü ve 95.000 px yükseklik. Her tuş vuruşunda bunun tamamı
// yeniden çizildiği için arama kutusu 200 ms'in üzerinde kilitleniyordu.
const CARDS_PER_PAGE = 20;

/**
 * Sayfa numarası şeridi: uçlar + geçerli sayfanın komşuları, arası "…".
 * 9 sayfada da 40 sayfada da tek satırda kalır.
 */
const buildPageList = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    // Uçlarda da üç komşu görünsün: 1. sayfada "1 2 3 … 9", ortada "1 … 4 5 6 … 9".
    let start = Math.max(2, current - 1);
    let end = Math.min(total - 1, current + 1);
    if (current <= 3) { start = 2; end = 4; }
    if (current >= total - 2) { start = total - 3; end = total - 1; }

    const middle = [];
    for (let p = start; p <= end; p += 1) middle.push(p);

    const unique = [...new Set([1, ...middle, total])].sort((a, b) => a - b);

    const list = [];
    unique.forEach((p, i) => {
        if (i > 0 && p - unique[i - 1] > 1) list.push('…');
        list.push(p);
    });
    return list;
};

const isValidPrice = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0;
};

// Fiyat alanı "199.90" gibi string gelir; input için string tutulur.
const asInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const planToEditState = (plan) => ({
    basePriceTry: asInputValue(plan.basePriceTry),
    isOpenGp: !!plan.isOpenGp,
    isOpenAp: !!plan.isOpenAp,
    isOpenWeb: !!plan.isOpenWeb,
});

const countryLabel = (country) => country.nameSelected || country.name;
const coverageLabel = (bundle) => (bundle.countries || []).map(countryLabel).join(', ');

const ESimPackagesPage = () => {
    const [allBundles, setAllBundles] = useState([]);
    const [countriesForFilter, setCountriesForFilter] = useState([]);

    const [selectedCountryName, setSelectedCountryName] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    // Yazmak ile filtrelemeyi ayırır: her tuşta liste yeniden çizilmez, kutu
    // takılmaz; filtreleme düşük öncelikte, kesilebilir şekilde çalışır.
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Varsayılan: yalnızca aktifler. Fiyatı girilmiş ama henüz açılmamış paketi
    // bulup aktif etmek için açılır.
    const [showInactive, setShowInactive] = useState(false);

    const [editingStates, setEditingStates] = useState({}); // { planId: {…} }
    const [savingStates, setSavingStates] = useState({});   // { planId: bool }
    const [priceErrors, setPriceErrors] = useState({});     // { planId: true } → alanı kırmızıya boya
    const [page, setPage] = useState(1);

    // --- API'den Veri Çekme ---
    const fetchPackagesFromApi = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchAdminBundles({
                family: 'standard',
                scope: 'country',
                includeInactive: showInactive ? undefined : false,
            });
            // Ülke sırası eski sayfadaki gibi alfabetik.
            const sorted = [...data].sort((a, b) => coverageLabel(a).localeCompare(coverageLabel(b), 'tr'));
            setAllBundles(sorted);

            // Ülke filtresi: bundle'ların kapsadığı ülkelerin birleşimi.
            const seen = new Set();
            sorted.forEach((bundle) => {
                (bundle.countries || []).forEach((country) => {
                    const label = countryLabel(country);
                    if (label) seen.add(label);
                });
            });
            setCountriesForFilter([...seen].sort((a, b) => a.localeCompare(b, 'tr')));

            const initialEdits = {};
            sorted.forEach((bundle) => {
                (bundle.plans || []).forEach((plan) => {
                    initialEdits[String(plan.id)] = planToEditState(plan);
                });
            });
            setEditingStates(initialEdits);
            setPriceErrors({});
        } catch (err) {
            console.error('Paket yükleme hatası:', err);
            setError(readApiError(err, 'Paketler yüklenirken bir hata oluştu.').message);
            setAllBundles([]);
        } finally {
            setIsLoading(false);
        }
    }, [showInactive]);

    useEffect(() => {
        fetchPackagesFromApi();
    }, [fetchPackagesFromApi]);

    // --- Filtrelenmiş Paketler ---
    const filteredBundles = useMemo(() => {
        let filtered = allBundles;

        if (selectedCountryName !== 'all') {
            filtered = filtered.filter((bundle) =>
                (bundle.countries || []).some((c) => countryLabel(c) === selectedCountryName)
            );
        }

        const term = deferredSearchTerm.trim().toLowerCase();
        if (term) {
            filtered = filtered.filter((bundle) =>
                (bundle.title || '').toLowerCase().includes(term)
                || (bundle.description || '').toLowerCase().includes(term)
                || coverageLabel(bundle).toLowerCase().includes(term)
                || (bundle.plans || []).some((plan) => (plan.title || '').toLowerCase().includes(term))
            );
        }

        return filtered;
    }, [allBundles, selectedCountryName, deferredSearchTerm]);

    const pageCount = Math.max(1, Math.ceil(filteredBundles.length / CARDS_PER_PAGE));
    // Filtre daraldığında elde olmayan bir sayfada kalmayalım.
    const currentPage = Math.min(page, pageCount);
    const firstIndex = (currentPage - 1) * CARDS_PER_PAGE;
    const visibleBundles = filteredBundles.slice(firstIndex, firstIndex + CARDS_PER_PAGE);

    // Filtre/arama değişince başa dön.
    useEffect(() => {
        setPage(1);
    }, [selectedCountryName, deferredSearchTerm, showInactive]);

    const goToPage = (next) => {
        setPage(next);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCountryFilterChange = (event) => setSelectedCountryName(event.target.value);
    const handleSearchChange = (event) => setSearchTerm(event.target.value);

    const handleInputChange = (planId, field, value) => {
        setEditingStates((prev) => ({ ...prev, [planId]: { ...prev[planId], [field]: value } }));
        // Kullanıcı alana dokunduğu anda kırmızı işaret kalkar.
        if (field === 'basePriceTry') {
            setPriceErrors((prev) => (prev[planId] ? { ...prev, [planId]: false } : prev));
        }
    };

    // Yayın kontrollerinden (aktif/pasif, Play) dönen alanları bundle'a işler.
    const mergeBundleIntoState = (bundleId, fields) => {
        setAllBundles((prev) => prev.map((bundle) => (bundle.id === bundleId ? { ...bundle, ...fields } : bundle)));
    };

    // Sunucudan dönen güncel planı listeye işler: fiyat kaydından sonra Google'ın
    // vergi dahil referans fiyatları ve priceId yenilenir, ekran da yenilenmeli.
    const mergePlanIntoState = (planId, nextPlan) => {
        setAllBundles((prev) =>
            prev.map((bundle) => ({
                ...bundle,
                plans: (bundle.plans || []).map((plan) =>
                    String(plan.id) === planId ? { ...plan, ...nextPlan } : plan
                ),
            }))
        );
    };

    const handleSaveChanges = async (plan) => {
        const planId = String(plan.id);
        const editedState = editingStates[planId];
        if (!editedState) return;

        const price = parseFloat(editedState.basePriceTry);
        if (isNaN(price) || price < 0) {
            setPriceErrors((prev) => ({ ...prev, [planId]: true }));
            toast.error('Lütfen geçerli bir fiyat girin.');
            return;
        }

        // Uç kısmi güncelleme yapar: yalnızca değişen alanlar gönderilir.
        const patch = buildPlanPatchFromEdit(editedState, planToEditState(plan));
        if (Object.keys(patch).length === 0) {
            toast.info('Değişiklik yok.');
            return;
        }

        setSavingStates((prev) => ({ ...prev, [planId]: true }));
        try {
            const updated = await updateBundlePlan(plan.id, patch);
            // Sunucu güncel planı döndürüyorsa onu kullan; döndürmüyorsa en azından
            // gönderdiğimiz alanları yansıt.
            const next = updated && updated.id ? updated : patch;
            mergePlanIntoState(planId, next);
            setEditingStates((prev) => ({ ...prev, [planId]: planToEditState({ ...plan, ...next }) }));
            toast.success(`Paket (ID: ${planId}) başarıyla güncellendi.`);
        } catch (err) {
            console.error(`Plan ${planId} güncellenirken API hatası:`, err);
            const { code, message } = readApiError(err, 'Paket güncellenirken bir hata oluştu.');
            if (code === 'INVALID_PRICE') {
                // Reddedilen değeri silmiyoruz: kullanıcı kendi girdisini düzeltecek.
                setPriceErrors((prev) => ({ ...prev, [planId]: true }));
            } else if (code !== 'STORE_PUSH_FAILED') {
                // STORE_PUSH_FAILED'de hiçbir alan yazılmadı; girdi kalsın ki tekrar
                // denenebilsin. Diğer hatalarda form sunucudaki değere döner.
                setEditingStates((prev) => ({ ...prev, [planId]: planToEditState(plan) }));
            }
            // Plan bulunamadıysa elimizdeki liste bayat: tazele.
            if (err?.response?.status === 404) fetchPackagesFromApi();
            toast.error(message);
        } finally {
            setSavingStates((prev) => ({ ...prev, [planId]: false }));
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>E-SIM Paketleri</h1>
                    <p className={styles.subtitle}>Ülke bazlı paketlerin fiyatlarını, mağaza durumlarını ve yayınını yönetin.</p>
                </div>
                <div className={styles.infoNotes}>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>Girilen fiyatlar vergi hariçtir.</span>
                    </div>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>
                            Fiyat değişikliği Play'e kendiliğinden gider. "Play'e yayınla" yalnızca yeni bir
                            paketi ilk kez satışa açarken gerekir.
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label htmlFor="countryFilter">Ülkeye Göre Filtrele:</label>
                    <select id="countryFilter" value={selectedCountryName} onChange={handleCountryFilterChange} className={styles.selectInput}>
                        <option value="all">Tüm Ülkeler</option>
                        {countriesForFilter.map(name => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="searchFilter">Paket veya Ürün Ara:</label>
                    <input
                        type="text"
                        id="searchFilter"
                        placeholder="Portugal, 1 GB, vs..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                    />
                </div>
                <div className={styles.filterToggle}>
                    <StatusToggle
                        label="Pasif paketleri göster"
                        checked={showInactive}
                        disabled={isLoading}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        onText="Pasifler gösteriliyor"
                        offText="Pasifleri göster"
                    />
                </div>
            </div>

            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loadingContainer}><LoadingSpinner /></div>
                ) : error ? (
                    <div className={styles.errorPanel}>
                        <span className={styles.errorIcon}>⚠️</span>
                        <p className={styles.errorMessage}>{error}</p>
                        <button className={`${styles.retryButton} ghost`} onClick={() => fetchPackagesFromApi()}>Tekrar dene</button>
                    </div>
                ) : filteredBundles.length === 0 ? (
                    <p className={styles.emptyState}>
                        Filtrelerinize uygun {showInactive ? '' : 'aktif '}paket bulunamadı.
                        {!showInactive && ' Henüz yayına alınmamış bir paketi arıyorsanız “Pasifleri göster”i açın.'}
                    </p>
                ) : (
                    <>
                    <div className={styles.resultBar}>
                        <span>
                            <strong className="num">{filteredBundles.length}</strong> üründen{' '}
                            <span className="num">{firstIndex + 1}-{Math.min(firstIndex + CARDS_PER_PAGE, filteredBundles.length)}</span> arası
                        </span>
                        {pageCount > 1 && (
                            <span className={styles.pageInfo}>
                                Sayfa <span className="num">{currentPage}</span> / <span className="num">{pageCount}</span>
                            </span>
                        )}
                    </div>

                    {visibleBundles.map((bundle) => (
                            <div key={bundle.id} className={styles.productCard}>
                                <div className={styles.productHeader}>
                                    {bundle.imgUrl && (
                                        <img src={bundle.imgUrl} alt={bundle.title} className={styles.productImage} />
                                    )}
                                    <div className={styles.productInfo}>
                                        <h2 className={styles.productName}>{bundle.title} <span className={styles.countryTag}>{coverageLabel(bundle)}</span></h2>
                                        <p className={styles.productDescription}>{bundle.description}</p>
                                    </div>
                                    <BundleControls bundle={bundle} onUpdated={mergeBundleIntoState} />
                                </div>
                                <div className={styles.tableWrap}>
                                <table className={styles.refillsTable}>
                                    <thead>
                                        <tr>
                                            <th>Paket</th>
                                            <th className={styles.numCol}>Fiyat (TL)</th>
                                            <th className={styles.toggleCol}>Google Play</th>
                                            <th className={styles.toggleCol}>App Store</th>
                                            <th className={styles.toggleCol}>Web</th>
                                            <th className={styles.actionCol}>İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortPlans(bundle.plans || []).map((plan) => {
                                            const planId = String(plan.id);
                                            const currentEditState = editingStates[planId] || {};
                                            const isSavingThisRow = savingStates[planId] || false;
                                            // Taban fiyatı olmayan planın Play durumu değiştirilemez (PRICE_REQUIRED).
                                            const hasBase = isValidPrice(currentEditState.basePriceTry)
                                                || isValidPrice(plan.basePriceTry);

                                            return (
                                                <tr key={planId}>
                                                    <td>
                                                        <div className="num">{plan.title}</div>
                                                        <div className={styles.planMeta}>
                                                            <span className="num">{formatDataAmount(plan)}</span>
                                                            <span> · </span>
                                                            <span className="num">{plan.validityDays ?? '—'} gün</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.numCol}>
                                                        <input type="number" value={currentEditState.basePriceTry || ''} onChange={(e) => handleInputChange(planId, 'basePriceTry', e.target.value)} className={`${styles.priceInput} ${priceErrors[planId] ? styles.inputError : ''}`} step="0.01" disabled={isSavingThisRow} aria-invalid={!!priceErrors[planId]} />
                                                        <div className={styles.taxPrices} title="Vergi dahil satış fiyatları">
                                                            <span className={styles.taxLabel}>Vergi dahil</span>
                                                            <span className={`${styles.taxVals} num`}>₺{formatMoney(plan.priceTry)} · €{formatMoney(plan.priceEur)} · ${formatMoney(plan.priceUsd)}</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch} title={hasBase ? undefined : 'Önce fiyat girip kaydedin'}>
                                                            <input type="checkbox" checked={currentEditState.isOpenGp || false} onChange={(e) => handleInputChange(planId, 'isOpenGp', e.target.checked)} disabled={isSavingThisRow || !hasBase} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenAp || false} onChange={(e) => handleInputChange(planId, 'isOpenAp', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenWeb || false} onChange={(e) => handleInputChange(planId, 'isOpenWeb', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.actionCol}>
                                                        <button onClick={() => handleSaveChanges(plan)} className={styles.saveButton} disabled={isSavingThisRow}>
                                                            {isSavingThisRow ? 'Kaydediliyor...' : 'Kaydet'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                </div>
                            </div>
                    ))}

                    {pageCount > 1 && (
                        <nav className={styles.pagination} aria-label="Sayfalama">
                            <button
                                type="button"
                                className={styles.pageButton}
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                ‹ Önceki
                            </button>

                            {buildPageList(currentPage, pageCount).map((item, i) => (
                                item === '…' ? (
                                    <span key={`gap-${i}`} className={styles.pageEllipsis}>…</span>
                                ) : (
                                    <button
                                        key={item}
                                        type="button"
                                        className={`${styles.pageButton} ${item === currentPage ? styles.pageActive : ''}`}
                                        aria-current={item === currentPage ? 'page' : undefined}
                                        onClick={() => goToPage(item)}
                                    >
                                        {item}
                                    </button>
                                )
                            ))}

                            <button
                                type="button"
                                className={styles.pageButton}
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage === pageCount}
                            >
                                Sonraki ›
                            </button>
                        </nav>
                    )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ESimPackagesPage;